/* =========================================================================
   Bulk Variant Order Popup
   Vanilla JS. No dependencies. Uses the Shopify AJAX Cart API to add many
   variants in a single request. Event-delegated so any number of popups can
   live on the page and the file can be loaded more than once safely.
   ========================================================================= */
(function () {
  'use strict';

  // Guard against double initialisation (snippet may be rendered > once).
  if (window.__bulkOrderPopupInit) return;
  window.__bulkOrderPopupInit = true;

  var SELECTORS = {
    modal: '[data-bulk-order]',
    row: '[data-bulk-row]',
    qtyInput: '[data-bulk-qty-input]',
    search: '[data-bulk-order-search]',
    empty: '[data-bulk-order-empty]',
    feedback: '[data-bulk-order-feedback]',
    addBtn: '[data-bulk-order-add]',
    addText: '[data-bulk-add-text]',
    addLoader: '[data-bulk-add-loader]'
  };

  var lastFocused = null;

  /* ----------------------------- Helpers ----------------------------- */

  function find(scope, selector) {
    return scope.querySelector(selector);
  }

  function findAll(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function clampQty(value) {
    var n = parseInt(value, 10);
    if (isNaN(n) || n < 0) n = 0;
    return n;
  }

  function setFeedback(modal, message, state) {
    var el = find(modal, SELECTORS.feedback);
    if (!el) return;
    el.className = 'bulk-order__feedback' + (state ? ' is-' + state : '');
    el.innerHTML = message || '';
  }

  /* ----------------------------- Open / close ------------------------ */

  function openModal(modal) {
    if (!modal) return;

    // Move the modal to <body> so `position: fixed` is resolved against the
    // viewport (not a transformed/overflow ancestor) — this guarantees the
    // popup is centred on screen and lives outside any <form>.
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }

    lastFocused = document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bulk-order-open');
    setFeedback(modal, '');

    // Focus the search field (or the dialog) for accessibility.
    var search = find(modal, SELECTORS.search);
    if (search) {
      window.setTimeout(function () {
        search.focus();
      }, 50);
    }
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bulk-order-open');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  /* ----------------------------- Search ------------------------------ */

  function filterRows(modal, term) {
    var query = (term || '').trim().toLowerCase();
    var rows = findAll(modal, SELECTORS.row);
    var visible = 0;

    rows.forEach(function (row) {
      var haystack = row.getAttribute('data-search') || '';
      var match = query === '' || haystack.indexOf(query) !== -1;
      row.hidden = !match;
      if (match) visible++;
    });

    var empty = find(modal, SELECTORS.empty);
    if (empty) empty.hidden = visible !== 0;
  }

  /* ----------------------------- Quantity ---------------------------- */

  function stepQty(input, delta) {
    var next = clampQty(input.value) + delta;
    if (next < 0) next = 0;
    input.value = next;
  }

  /* ----------------------------- Add to cart ------------------------- */

  function collectItems(modal) {
    var items = [];
    findAll(modal, SELECTORS.row).forEach(function (row) {
      var input = find(row, SELECTORS.qtyInput);
      if (!input) return; // sold-out rows have no input
      var qty = clampQty(input.value);
      if (qty > 0) {
        items.push({
          id: parseInt(row.getAttribute('data-variant-id'), 10),
          quantity: qty
        });
      }
    });
    return items;
  }

  function setLoading(modal, isLoading) {
    var btn = find(modal, SELECTORS.addBtn);
    var text = find(modal, SELECTORS.addText);
    var loader = find(modal, SELECTORS.addLoader);
    if (!btn) return;
    btn.disabled = isLoading;
    if (text) text.hidden = isLoading;
    if (loader) loader.hidden = !isLoading;
  }

  function addItems(modal) {
    var items = collectItems(modal);

    if (items.length === 0) {
      setFeedback(modal, 'Please choose a quantity for at least one item.', 'error');
      return;
    }

    setLoading(modal, true);
    setFeedback(modal, '');

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          // Shopify returns { description, message } on failure (e.g. stock).
          var msg =
            (result.data && (result.data.description || result.data.message)) ||
            'Sorry, these items could not be added to your cart.';
          setFeedback(modal, msg, 'error');
          setLoading(modal, false);
          return;
        }

        // Success — let the theme / other apps know the cart changed…
        document.dispatchEvent(
          new CustomEvent('bulk-order:added', {
            bubbles: true,
            detail: { items: items }
          })
        );

        // …then send the customer to the cart page so they see the updated
        // cart. (The spinner stays visible until the page navigates away.)
        setFeedback(modal, 'Items added — taking you to your cart…', 'success');
        var cartUrl = modal.getAttribute('data-cart-url') || '/cart';
        window.location.assign(cartUrl);
      })
      .catch(function (error) {
        setFeedback(
          modal,
          'Something went wrong. Please try again.',
          'error'
        );
        setLoading(modal, false);
        // eslint-disable-next-line no-console
        console.error('[bulk-order]', error);
      });
  }

  /* ----------------------------- Delegated events -------------------- */

  document.addEventListener('click', function (event) {
    var target = event.target;

    // Open
    var opener = target.closest('[data-bulk-order-open]');
    if (opener) {
      event.preventDefault();
      var id = opener.getAttribute('data-bulk-order-open');
      openModal(document.getElementById(id));
      return;
    }

    // Close (overlay, header X, footer Close)
    var closer = target.closest('[data-bulk-order-close]');
    if (closer) {
      event.preventDefault();
      closeModal(target.closest(SELECTORS.modal));
      return;
    }

    // Quantity minus / plus
    var minus = target.closest('[data-bulk-minus]');
    var plus = target.closest('[data-bulk-plus]');
    if (minus || plus) {
      var qtyWrap = target.closest('[data-bulk-qty]');
      var input = qtyWrap && find(qtyWrap, SELECTORS.qtyInput);
      if (input) stepQty(input, minus ? -1 : 1);
      return;
    }

    // Add items — stop the event so no theme/form handler can hijack it
    // and cause a full-page navigation to /cart.
    var addBtn = target.closest('[data-bulk-order-add]');
    if (addBtn) {
      event.preventDefault();
      event.stopPropagation();
      addItems(target.closest(SELECTORS.modal));
      return;
    }
  });

  // Sanitise manual typing in quantity fields.
  document.addEventListener('input', function (event) {
    var input = event.target;
    if (input.matches && input.matches(SELECTORS.qtyInput)) {
      var cleaned = input.value.replace(/[^\d]/g, '');
      if (cleaned !== input.value) input.value = cleaned;
    }

    if (input.matches && input.matches(SELECTORS.search)) {
      filterRows(input.closest(SELECTORS.modal), input.value);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (input.matches && input.matches(SELECTORS.qtyInput)) {
      input.value = clampQty(input.value);
    }
  });

  // Close on Escape.
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      var openModalEl = document.querySelector('.bulk-order.is-open');
      if (openModalEl) closeModal(openModalEl);
    }
  });
})();
